import React, { useState, useEffect, Component } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import i18n from "./i18n";
import MapView from "./Components/Map/MapView";
import Profile from "./Components/Profile";
import UploadView from "./Components/Upload/UploadView";
import LoginPage from "./Components/Auth/LoginPage";
import RegisterPage from "./Components/Auth/RegisterPage";
import VerifyEmail from "./Components/Auth/VerifyEmail";
import ForgotPassword from "./Components/Auth/ForgotPassword";
import CitiesIndex from "./Components/Explore/CitiesIndex";
import CityPage from "./Components/Explore/CityPage";
import PostDetailView from "./Components/Post/PostDetailView";
import EditProfile from "./Components/EditProfile";
import Notifications from "./Components/Notifications";
import Navigation from "./Components/Navigation";
import HeroLanding from "./Components/Landing/HeroLanding";
import PrivacyPolicy from "./Components/PrivacyPolicy";
import SettingsPage from "./Components/SettingsPage";
import TermsOfService from "./Components/TermsOfService";
import { isNative, onAppUrl, onPushTapped, onBackButton } from "./utils/native";
import { getOrganizationSchema } from "./utils/seo";

class ErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("App crash:", error, info);
  }

  render() {
    if (this.state.hasError) {
      const t = i18n.t;
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-th-bg text-th-text p-6 text-center">
          <h1 className="text-2xl font-bold mb-2">{t("app.crashed")}</h1>
          <p className="text-th-text-sec mb-4">{t("app.crashMessage")}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-th-accent text-white rounded-lg">
            {t("app.reload")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Handles deep links + push notification taps inside the Router context */
function NativeListeners() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative) return;

    // Deep links: reroute.app/profile/username → navigate there
    onAppUrl(({ url }) => {
      try {
        const path = new URL(url).pathname;
        if (path && path !== "/") navigate(path);
      } catch {
        // ignore malformed URLs
      }
    });

    // Android back button
    onBackButton(({ canGoBack }) => {
      if (canGoBack) {
        navigate(-1);
      }
    });

    // Push notification tapped → go to the relevant screen
    onPushTapped(({ notification }) => {
      const data = notification?.data;
      if (data?.postId && data?.username) {
        navigate(`/profile/${data.username}/post/${data.postId}`);
      } else if (data?.type === "comment") {
        navigate("/notifications");
      }
    });
  }, [navigate]);

  return null;
}

function HomePage({ activeCategory, onCategoryChange }) {
  return <MapView activeCategory={activeCategory} onCategoryChange={onCategoryChange} />;
}

// Old share links used /@username — send them to the profile route
function LegacyProfileRedirect() {
  const { pathname } = window.location;
  const username = pathname.startsWith("/@") ? pathname.slice(2).split("/")[0] : "";
  return <Navigate to={username ? `/profile/${username}` : "/"} replace />;
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="h-full flex flex-col items-center justify-center bg-th-bg px-6 text-center">
      <p className="text-5xl mb-4">📍</p>
      <p className="text-lg font-bold text-th-text">{t("notFound.title", "This spot doesn't exist")}</p>
      <p className="text-sm text-th-muted mt-2">{t("notFound.body", "The page you're looking for isn't on the map.")}</p>
      <Link to="/" className="mt-5 text-sm font-medium text-th-bg bg-th-accent px-5 py-2.5 rounded-xl">
        {t("notFound.cta", "Back to the map")}
      </Link>
    </div>
  );
}

function App() {
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(getOrganizationSchema());
    script.id = "json-ld-organization";
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <NativeListeners />
          <div className="app-shell flex flex-col overflow-hidden bg-th-bg">
            <div className="flex-1 overflow-hidden">
              <Routes>
                <Route
                  path="/"
                  element={
                    <HomePage
                      activeCategory={activeCategory}
                      onCategoryChange={setActiveCategory}
                    />
                  }
                />
                <Route path="/upload" element={<UploadView />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/profile/edit" element={<EditProfile />} />
                <Route path="/profile/:userId" element={<Profile />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/profile/:username/post/:postId" element={<PostDetailView />} />
                <Route path="/post/:postId" element={<PostDetailView />} />
                <Route path="/welcome" element={<HeroLanding />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/explore" element={<CitiesIndex />} />
                <Route path="/explore/:city" element={<CityPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/@:username" element={<LegacyProfileRedirect />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
            <Navigation />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
